"use client";

import { useState } from "react";
import { api } from "@/lib/trpc/client";

interface ChainWalletExplorerProps {
  slug: string;
  chainName: string;
}

function formatUsd(value: number | null | undefined): string {
  if (value == null) return "--";
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  if (value >= 1e3) return `$${(value / 1e3).toFixed(2)}K`;
  return `$${value.toFixed(2)}`;
}

function shortenAddress(addr: string): string {
  if (addr.length <= 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function timeAgo(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function ChainWalletExplorer({ slug, chainName }: ChainWalletExplorerProps) {
  const [address, setAddress] = useState("");
  const [searchAddress, setSearchAddress] = useState("");
  const [txPage, setTxPage] = useState(1);
  const [activeTab, setActiveTab] = useState<"holdings" | "transactions">("holdings");

  const { data: walletData, isLoading: loadingWallet, error: walletError } =
    api.onchain.getWalletOverview.useQuery(
      { address: searchAddress, chainSlug: slug },
      { enabled: searchAddress.length > 0, staleTime: 5 * 60 * 1000 },
    );

  const { data: txData, isLoading: loadingTx } =
    api.onchain.getWalletTransactions.useQuery(
      { address: searchAddress, chainSlug: slug, page: txPage },
      { enabled: searchAddress.length > 0 && activeTab === "transactions", staleTime: 5 * 60 * 1000 },
    );

  const handleSearch = () => {
    const trimmed = address.trim();
    if (trimmed.length > 0) {
      setSearchAddress(trimmed);
      setTxPage(1);
      setActiveTab("holdings");
    }
  };

  return (
    <div>
      {/* Search bar */}
      <div className="flex gap-2 mb-6">
        <input
          type="text"
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && handleSearch()}
          placeholder={`Enter ${chainName} wallet address...`}
          className="flex-1 px-4 py-2.5 rounded-lg bg-white/5 border border-[hsl(230,15%,20%)] text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 text-sm"
        />
        <button
          onClick={handleSearch}
          disabled={!address.trim()}
          className="px-5 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Explore
        </button>
      </div>

      {/* Loading state */}
      {loadingWallet && (
        <div className="text-center py-12 text-gray-500">
          Fetching wallet data...
        </div>
      )}

      {/* Error state */}
      {walletError && searchAddress && (
        <div className="text-center py-12 text-red-400 text-sm">
          {walletError.message}
        </div>
      )}

      {/* Wallet data */}
      {walletData && (
        <div>
          {/* Summary */}
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-[hsl(230,15%,15%)]">
            <div>
              <p className="text-xs text-gray-500 mb-1">Wallet</p>
              <p className="text-sm font-mono text-white">{searchAddress}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 mb-1">Total Value</p>
              <p className="text-xl font-bold text-white">
                {formatUsd(walletData.totalValueUsd)}
              </p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mb-4">
            <button
              onClick={() => setActiveTab("holdings")}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "holdings"
                  ? "bg-blue-600 text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
            >
              Holdings ({walletData.holdings.length})
            </button>
            <button
              onClick={() => setActiveTab("transactions")}
              className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                activeTab === "transactions"
                  ? "bg-blue-600 text-white"
                  : "bg-white/5 text-gray-400 hover:bg-white/10"
              }`}
            >
              Transactions
            </button>
          </div>

          {/* Holdings table */}
          {activeTab === "holdings" && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-gray-500 text-left text-xs">
                    <th className="pb-2 font-medium">Token</th>
                    <th className="pb-2 font-medium text-right">Price</th>
                    <th className="pb-2 font-medium text-right">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {walletData.holdings.map((h, i) => (
                    <tr
                      key={`${h.contractAddress}-${i}`}
                      className="border-t border-[hsl(230,15%,12%)]"
                    >
                      <td className="py-2.5">
                        <div className="flex items-center gap-2">
                          {h.logoUrl ? (
                            <img
                              src={h.logoUrl}
                              alt={h.symbol ?? ""}
                              className="w-6 h-6 rounded-full"
                            />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-400">
                              {(h.symbol ?? "?").charAt(0)}
                            </div>
                          )}
                          <div>
                            <span className="text-white font-medium">
                              {h.symbol ?? h.name}
                            </span>
                            {h.name && h.name !== h.symbol && (
                              <span className="text-gray-500 ml-1.5 text-xs">
                                {h.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="py-2.5 text-right text-gray-300 font-mono">
                        {h.price != null ? formatUsd(h.price) : "--"}
                      </td>
                      <td className="py-2.5 text-right text-white font-mono font-medium">
                        {formatUsd(h.usdValue)}
                      </td>
                    </tr>
                  ))}
                  {walletData.holdings.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-8 text-center text-gray-500">
                        No holdings found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          {/* Transactions table */}
          {activeTab === "transactions" && (
            <div>
              {loadingTx ? (
                <div className="text-center py-8 text-gray-500">
                  Loading transactions...
                </div>
              ) : txData ? (
                <>
                  {txData.nativeBalance != null && (
                    <p className="text-xs text-gray-500 mb-3">
                      Native Balance: {txData.nativeBalance} {txData.nativeSymbol}
                    </p>
                  )}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-gray-500 text-left text-xs">
                          <th className="pb-2 font-medium">Hash</th>
                          <th className="pb-2 font-medium">From</th>
                          <th className="pb-2 font-medium">To</th>
                          <th className="pb-2 font-medium text-right">Value</th>
                          <th className="pb-2 font-medium text-right">When</th>
                        </tr>
                      </thead>
                      <tbody>
                        {txData.transactions.map((tx) => (
                          <tr
                            key={tx.hash}
                            className="border-t border-[hsl(230,15%,12%)]"
                          >
                            <td className="py-2 font-mono text-blue-400 text-xs">
                              {shortenAddress(tx.hash)}
                            </td>
                            <td className="py-2 font-mono text-xs text-gray-300">
                              {tx.from ? shortenAddress(tx.from) : "--"}
                            </td>
                            <td className="py-2 font-mono text-xs text-gray-300">
                              {tx.to ? shortenAddress(tx.to) : "--"}
                            </td>
                            <td className="py-2 text-right text-white font-mono text-xs">
                              {tx.valueEth ?? "--"}
                            </td>
                            <td className="py-2 text-right text-gray-500 text-xs">
                              {tx.timestamp ? timeAgo(tx.timestamp) : "--"}
                            </td>
                          </tr>
                        ))}
                        {txData.transactions.length === 0 && (
                          <tr>
                            <td colSpan={5} className="py-8 text-center text-gray-500">
                              No transactions found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <button
                      onClick={() => setTxPage((p) => Math.max(1, p - 1))}
                      disabled={txPage === 1}
                      className="px-3 py-1 text-xs rounded bg-white/5 text-gray-400 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    <span className="text-xs text-gray-500">Page {txPage}</span>
                    <button
                      onClick={() => setTxPage((p) => p + 1)}
                      disabled={!txData.hasMore}
                      className="px-3 py-1 text-xs rounded bg-white/5 text-gray-400 hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No transaction data available.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Empty state (no search yet) */}
      {!searchAddress && !loadingWallet && (
        <div className="text-center py-12 text-gray-500 text-sm">
          Enter a wallet address to explore holdings and transactions on {chainName}.
        </div>
      )}
    </div>
  );
}
